import { useMemo, useState, useEffect, Fragment } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowUp, ArrowDown, ChevronsUpDown, ZoomIn, ZoomOut, RotateCcw, ChevronDown } from "lucide-react";
import { getTableZoom, setTableZoom, subscribeTableZoom, attachZoomKeys, detachZoomKeys, ZOOM_MIN, ZOOM_MAX } from "@/lib/tableZoom";

// columns: [{ key, label, sortable, filter, align, render(row), sortValue(row), filterValue(row) }]
// zoomControl: tampilkan tombol -/+/reset di atas tabel (zoom global; juga bisa via Ctrl +/-/0)
export function DataTable({ columns, rows, testid = "data-table", emptyText = "Belum ada data", rowClassName, groupBy, zoomable = false, zoomControl = true }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [filters, setFilters] = useState({});
  // Pemisah bulan dgn siklus 4 fase (klik berulang pada header bulan):
  //  fase 0 = terbuka penuh (bulan + tanggal + transaksi tampil)
  //  fase 1 = transaksi disembunyikan, header tanggal TETAP tampil
  //  fase 2 = bulan tertutup total (tanggal + transaksi disembunyikan)
  //  fase 3 = header tanggal tampil lagi, transaksi masih disembunyikan
  //  klik dari fase 3 -> kembali ke fase 0 (semua terbuka)
  const [monthPhase, setMonthPhase] = useState(() => new Map());
  const [collapsedDays, setCollapsedDays] = useState(() => new Set());
  const cycleMonth = (gk) => setMonthPhase((m) => { const n = new Map(m); n.set(gk, ((n.get(gk) || 0) + 1) % 4); return n; });
  const toggleDay = (key) => setCollapsedDays((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const [zoom, setZoomState] = useState(getTableZoom);
  useEffect(() => {
    attachZoomKeys();
    const unsub = subscribeTableZoom(setZoomState);
    return () => { unsub(); detachZoomKeys(); };
  }, []);
  const applyZoom = (z) => setTableZoom(z);
  const showZoom = zoomControl || zoomable;

  const filterCols = columns.filter((c) => c.filter);

  const distinct = (c) => {
    const vals = new Set();
    rows.forEach((r) => {
      const v = c.filterValue ? c.filterValue(r) : r[c.key];
      if (v !== undefined && v !== null && v !== "") vals.add(String(v));
    });
    return [...vals].sort();
  };

  const processed = useMemo(() => {
    let out = [...rows];
    for (const c of filterCols) {
      const fv = filters[c.key];
      if (fv && fv !== "__all") {
        out = out.filter((r) => String(c.filterValue ? c.filterValue(r) : r[c.key]) === fv);
      }
    }
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      out.sort((a, b) => {
        let va = col?.sortValue ? col.sortValue(a) : a[sortKey];
        let vb = col?.sortValue ? col.sortValue(b) : b[sortKey];
        if (va == null) va = "";
        if (vb == null) vb = "";
        if (typeof va === "number" && typeof vb === "number") return sortDir === "asc" ? va - vb : vb - va;
        return sortDir === "asc"
          ? String(va).localeCompare(String(vb))
          : String(vb).localeCompare(String(va));
      });
    }
    return out;
  }, [rows, filters, sortKey, sortDir, columns, filterCols]);

  const toggleSort = (c) => {
    if (!c.sortable) return;
    if (sortKey === c.key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(c.key); setSortDir("asc"); }
  };

  return (
    <div className="space-y-3 min-w-0">
      {filterCols.length > 0 && (
        <div className="flex flex-wrap gap-3" data-testid={`${testid}-filters`}>
          {filterCols.map((c) => (
            <div key={c.key} className="flex-1 min-w-[130px] sm:flex-none sm:w-[170px]">
              <Label className="text-[11px] text-muted-foreground">{c.label}</Label>
              <Select
                value={filters[c.key] || "__all"}
                onValueChange={(v) => setFilters((f) => ({ ...f, [c.key]: v }))}
              >
                <SelectTrigger data-testid={`${testid}-filter-${c.key}`} className="h-9">
                  <SelectValue placeholder="Semua" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Semua</SelectItem>
                  {distinct(c).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}

      {showZoom && (
        <div className="flex items-center justify-end gap-1.5" data-testid={`${testid}-zoom`}>
          <span className="text-xs text-muted-foreground mr-0.5 hidden sm:inline">Ukuran font tabel (Ctrl +/−)</span>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" data-testid={`${testid}-zoom-out`} title="Perkecil font tabel (Ctrl −) — lebar tabel tetap penuh" onClick={() => applyZoom(zoom - 0.1)} disabled={zoom <= ZOOM_MIN}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-xs font-medium w-11 text-center tabular-nums" data-testid={`${testid}-zoom-level`}>{Math.round(zoom * 100)}%</span>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" data-testid={`${testid}-zoom-in`} title="Perbesar font tabel (Ctrl +) — lebar tabel tetap penuh" onClick={() => applyZoom(zoom + 0.1)} disabled={zoom >= ZOOM_MAX}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" data-testid={`${testid}-zoom-reset`} title="Reset (Ctrl 0)" onClick={() => applyZoom(1)} disabled={zoom === 1}>
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div className="rounded-lg border border-border overflow-x-auto">
        <Table data-testid={testid} data-dt-zoom style={{ "--dt-scale": zoom }}>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead
                  key={c.key}
                  onClick={() => toggleSort(c)}
                  data-testid={`${testid}-head-${c.key}`}
                  className={`${c.sortable ? "cursor-pointer select-none hover:text-foreground" : ""} ${c.align === "right" ? "text-right" : ""} whitespace-normal break-words`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {c.sortable && (
                      sortKey === c.key
                        ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
                        : <ChevronsUpDown className="h-3 w-3 opacity-40" />
                    )}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {processed.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center text-sm text-muted-foreground py-10">
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : (
              processed.map((r, i) => {
                const rowEl = (
                  <TableRow key={r.id || i} data-testid={`${testid}-row-${r.id || i}`} className={`animate-in fade-in-0 slide-in-from-top-1 duration-200 ${rowClassName ? rowClassName(r) : ""}`}>
                    {columns.map((c) => (
                      <TableCell key={c.key} className={`${c.align === "right" ? "text-right" : ""} whitespace-normal break-words`}>
                        {c.render ? c.render(r) : (r[c.key] ?? "-")}
                      </TableCell>
                    ))}
                  </TableRow>
                );
                if (groupBy) {
                  const gk = groupBy.key(r);
                  const prevGk = i > 0 ? groupBy.key(processed[i - 1]) : null;
                  const monthChanged = gk !== prevGk;
                  const hasSub = typeof groupBy.subKey === "function";
                  const sk = hasSub ? groupBy.subKey(r) : null;
                  const prevSk = hasSub && i > 0 ? groupBy.subKey(processed[i - 1]) : null;
                  const dayChanged = hasSub && (monthChanged || sk !== prevSk);
                  const phase = monthPhase.get(gk) || 0;
                  const monthCollapsed = phase === 2;     // tanggal + transaksi disembunyikan
                  const daysVisible = phase !== 2;         // header tanggal tampil (fase 0/1/3)
                  const trxVisible = phase === 0;          // transaksi tampil (fase 0 saja)
                  const dayKey = `${gk}||${sk}`;
                  const dayCollapsed = hasSub && collapsedDays.has(dayKey);
                  const showRow = trxVisible && !dayCollapsed;
                  if (monthChanged || dayChanged || showRow) {
                    return (
                      <Fragment key={`grp-${gk}-${sk}-${r.id || i}`}>
                        {monthChanged && (
                          <TableRow className="bg-muted/60 hover:bg-muted/70 border-y cursor-pointer select-none transition-colors" onClick={() => cycleMonth(gk)} data-testid={`${testid}-month-${gk}`}>
                            <TableCell colSpan={columns.length} className="py-2">
                              <span className="inline-flex items-center gap-2 text-base font-bold uppercase tracking-wide text-foreground">
                                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${monthCollapsed ? "-rotate-90" : ""}`} />
                                {groupBy.label(r)}
                                {(phase === 1 || phase === 3) && (
                                  <span className="text-[10px] font-medium normal-case tracking-normal text-muted-foreground bg-background/70 border rounded px-1.5 py-0.5">tanggal saja</span>
                                )}
                              </span>
                            </TableCell>
                          </TableRow>
                        )}
                        {dayChanged && daysVisible && (
                          <TableRow className="bg-muted/25 hover:bg-muted/40 cursor-pointer select-none transition-colors animate-in fade-in-0 slide-in-from-top-1 duration-200" onClick={() => toggleDay(dayKey)} data-testid={`${testid}-day-${dayKey}`}>
                            <TableCell colSpan={columns.length} className="py-1.5 pl-6">
                              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground/80">
                                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${(dayCollapsed || !trxVisible) ? "-rotate-90" : ""}`} />
                                {groupBy.subLabel(r)}
                              </span>
                            </TableCell>
                          </TableRow>
                        )}
                        {showRow && rowEl}
                      </Fragment>
                    );
                  }
                  return null;
                }
                return rowEl;
              })
            )}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">{processed.length} baris</p>
    </div>
  );
}
